import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import api from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  ShieldCheck
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function PayPalPayment({ booking, onPaymentSuccess, onPaymentError }) {
  const { isAdmin } = useAuth();
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(booking?.payment_status || 'unpaid');

  useEffect(function() {
    loadPayPalConfig();
  }, []);

  async function loadPayPalConfig() {
    try {
      const response = await api.get('/payments/config');
      setPaypalConfig(response.data);
    } catch (err) {
      console.error('Failed to load PayPal config:', err);
    } finally {
      setLoading(false);
    }
  }

  async function createOrder() {
    try {
      const response = await api.post('/payments/create-order', {
        booking_id: booking.id,
        amount: booking.price,
        currency: 'USD',
        description: `Shipping Booking ${booking.booking_number}`
      });
      return response.data.order_id;
    } catch (err) {
      console.error('Failed to create PayPal order:', err);
      toast.error(err.response?.data?.detail || 'Failed to create payment order');
      throw err;
    }
  }

  async function onApprove(data) {
    setProcessing(true);
    try {
      const response = await api.post('/payments/capture', {
        order_id: data.orderID,
        booking_id: booking.id
      });
      
      if (response.data.status === 'success') {
        setPaymentStatus('paid');
        toast.success('Payment successful! Your booking is confirmed.');
        if (onPaymentSuccess) {
          onPaymentSuccess(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to capture payment:', err);
      toast.error(err.response?.data?.detail || 'Payment capture failed');
      if (onPaymentError) {
        onPaymentError(err);
      }
    } finally {
      setProcessing(false);
    }
  }

  function onError(err) {
    console.error('PayPal error:', err);
    toast.error('Payment failed. Please try again.');
    if (onPaymentError) {
      onPaymentError(err);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <div className="bg-green-50 rounded-sm p-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-medium tracking-[-0.01em] text-green-800 mb-2">Payment Complete</h3>
        <p className="text-green-600">Your booking has been paid and confirmed.</p>
        <Badge className="bg-green-100 text-green-700 mt-4">
          Paid: {formatCurrency(booking.price)}
        </Badge>
      </div>
    );
  }

  if (!paypalConfig?.configured) {
    return (
      <div className="bg-amber-50 rounded-sm p-6" data-testid="paypal-not-configured">
        <div className="flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-amber-800 mb-2">PayPal Not Configured</h3>
            <p className="text-amber-700 text-sm mb-4">
              Payment processing is not available. Please contact support or pay later.
            </p>
            {paypalConfig?.setup_instructions && isAdmin && (
              <div className="bg-white rounded-sm p-4 text-sm" data-testid="paypal-admin-setup">
                <p className="font-medium text-gray-700 mb-2">Admin Setup:</p>
                <ol className="list-decimal list-outside pl-5 space-y-1 text-gray-600 break-words">
                  <li>{paypalConfig.setup_instructions.step1}</li>
                  <li>{paypalConfig.setup_instructions.step2}</li>
                  <li>{paypalConfig.setup_instructions.step3}</li>
                  <li>{paypalConfig.setup_instructions.step4}</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="paypal-payment">
      {/* Payment Summary */}
      <div className="bg-gray-50 rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-ink flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Payment Details
          </h3>
          <Badge className="bg-amber-100 text-amber-700">Pending Payment</Badge>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Booking Number</span>
            <span className="sv-mono font-medium">{booking.booking_number}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Route</span>
            <span className="font-medium">{booking.origin_port?.split(',')[0]} → {booking.destination_port?.split(',')[0]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Containers</span>
            <span className="font-medium">{booking.container_count}× {booking.container_type}</span>
          </div>
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="flex justify-between items-center">
              <span className="font-medium text-ink">Total Amount</span>
              <span className="text-2xl font-normal tracking-[-0.01em] text-blue-600">{formatCurrency(booking.price)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
        <ShieldCheck className="h-4 w-4 text-green-500" />
        <span>Secure payment powered by PayPal</span>
      </div>

      {/* PayPal Buttons */}
      {processing ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Processing payment...</span>
        </div>
      ) : (
        <PayPalScriptProvider options={{ 
          clientId: paypalConfig.paypal_client_id,
          currency: "USD"
        }}>
          <PayPalButtons
            style={{
              layout: "vertical",
              color: "blue",
              shape: "pill",
              label: "pay"
            }}
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onError}
            onCancel={function() {
              toast.info('Payment cancelled');
            }}
          />
        </PayPalScriptProvider>
      )}

      {/* Alternative Payment Note */}
      <p className="text-center text-sm text-gray-500">
        You can also pay with credit/debit card through PayPal without creating an account
      </p>
    </div>
  );
}

export default PayPalPayment;
